import React, { useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { StacApiProvider } from '@developmentseed/stac-react';
import { PluginConfigProvider } from '@stac-manager/data-core';

import { App } from './App';
import system from './theme/theme';
import { config } from './plugin-system/config';
import { AuthProvider, useAuth } from './auth/Context';
import Api, { ApiContext } from './api';
import { Toaster } from './components/Toaster';

const publicUrl = process.env.PUBLIC_URL || '';
const stacApiUrl = process.env.REACT_APP_STAC_API!;

let basename: string | undefined;
if (publicUrl) {
  try {
    basename = new URL(publicUrl).pathname;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // no-op
  }
}

// Single source of truth for auth-to-API wiring. Reads the current OIDC token
// from useAuth() and feeds it into two consumers:
//   1. ApiContext — for direct Api.fetch calls (mutations, ad-hoc reads).
//   2. StacApiProvider — for stac-react hooks (useCollection, useStacSearch…).
// A token change rebuilds both: a new Api instance via useMemo, and a new
// `options` identity that triggers stac-react's useStacApi effect to rebuild
// its StacApi with fresh headers.
//
// During OIDC load we render children without StacApiProvider so the rest of
// the app can still mount and read from ApiContext. NOTE: a stac-react hook
// rendered without the provider throws (the only QueryClientProvider lives
// inside StacApiProvider) — this is safe solely because App.tsx swaps all
// routes for a spinner behind the same auth isLoading flag, so no hook
// consumer mounts while the provider is absent. Keep those two gates in sync.
function StacApiAuthBridge({ children }: { children: React.ReactNode }) {
  const { token, isLoading, refreshAuth } = useAuth();

  const api = useMemo(
    () => new Api(token, undefined, refreshAuth),
    [token, refreshAuth]
  );
  const options = useMemo(
    () =>
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    [token]
  );

  return (
    <ApiContext.Provider value={api}>
      {isLoading ? (
        children
      ) : (
        <StacApiProvider apiUrl={stacApiUrl} options={options}>
          {children}
        </StacApiProvider>
      )}
    </ApiContext.Provider>
  );
}

// Root component.
function Root() {
  useEffect(() => {
    // Hide the welcome banner.
    const banner = document.querySelector('#welcome-banner');
    if (!banner) return;
    banner.classList.add('dismissed');
    setTimeout(() => banner.remove(), 500);
  }, []);

  return (
    <React.StrictMode>
      <ChakraProvider value={system}>
        <Toaster />
        <Router basename={basename}>
          <AuthProvider>
            <StacApiAuthBridge>
              <PluginConfigProvider config={config}>
                <App />
              </PluginConfigProvider>
            </StacApiAuthBridge>
          </AuthProvider>
        </Router>
      </ChakraProvider>
    </React.StrictMode>
  );
}

const rootNode = document.querySelector('#app-container')!;
const root = createRoot(rootNode);
root.render(<Root />);
